import { Location } from '@angular/common';
import { Injectable, TemplateRef } from '@angular/core';
import { StrictOmit } from '@epicuro-next/utilities';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, fromEvent, map, Observable, of } from 'rxjs';

import {
  Choice,
  GenericModalComponent,
} from '../components/generic-modal/generic-modal.component';
import { ModalOptions } from '../interfaces/modal-options';
import { ClosableModal } from '../models/modal-closable';
import { ModalRef } from '../models/modal-ref';

export type GenericModalRef = StrictOmit<
  StrictOmit<
    { [key in keyof ModalRef]: ModalRef[key] },
    'componentInstance'
  > & { componentInstance: GenericModalComponent },
  'result'
> & {
  result: Observable<Choice['value']>;
};
@Injectable()
export class ModalService {
  private defaultOptions: ModalOptions = {
    backdrop: true,
    scrollable: false,
    attachBrowserHistory: false,
    centered: false,
  };

  private modalsSet = new Set<ClosableModal>();

  constructor(private modalService: NgbModal, private location: Location) {
    fromEvent(window, 'popstate')
      // stop listening when user change browser state
      .subscribe(() => {
        const lastOpenedModal: ClosableModal | undefined = Array.from(
          this.modalsSet,
        )?.reverse()?.[0];

        if (lastOpenedModal) {
          lastOpenedModal.closeModal();
          this.modalsSet.delete(lastOpenedModal);
        }
      });
  }

  public open(
    content: string | TemplateRef<unknown> | unknown,
    options?: Partial<ModalOptions>,
  ): ModalRef {
    const ngbModalRef: NgbModalRef = this.modalService.open(content, {
      ...this.defaultOptions,
      ...options,
    });

    if (options?.attachBrowserHistory) {
      // patching modal
      this.modalsSet.add(new ClosableModal(ngbModalRef, this.location));
    }

    return new ModalRef(ngbModalRef);
  }

  public openGenericModal(
    choices: Choice[],
    noChoiceValue: Choice['value'],
    message: string,
    title?: string,
  ): GenericModalRef {
    const ngbModalRef: NgbModalRef = this.modalService.open(
      GenericModalComponent,
      {
        ...this.defaultOptions,
      },
    );

    const mRef = new ModalRef(ngbModalRef);
    const modalRef: GenericModalRef = {
      result: mRef.result.pipe(
        catchError((errorValue) => {
          if (errorValue === 0) {
            return of(noChoiceValue);
          }
          throw errorValue;
        }),
        map((answer) => {
          return answer as Choice['value'];
        }),
      ),
      close: mRef.close,
      componentInstance: mRef.componentInstance,
      dismiss: mRef.dismiss,
      onDismiss: mRef.onDismiss,
    };

    modalRef.componentInstance.choices = choices;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.title = title;

    return modalRef;
  }
}
